import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Lock, MapPin, ArrowRight } from 'lucide-react';
import { GOLD, NAVY, alpha } from '../theme/palette';
import { useTourTracking } from '../utils/analytics';

// Belmond Cathedral theme tokens
const SERIF = { fontFamily: '"Fraunces", serif' };

// Normalize text for client-side search:
// lowercase + strip accents + collapse whitespace. Keeps letters/numbers/space
// so word-order independent token matching works intuitively.
export const normalizeForSearch = (s) =>
  (s == null ? '' : String(s))
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const stripHtml = (html) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  } catch { return String(html); }
};

/**
 * Build a searchable string ("haystack") for a tour across all languages.
 * Keeps the signature identical for Home and /pilgrimages so a single helper
 * powers both pages.
 */
export const buildTourHaystack = (tour, language = 'pt') => {
  const dateFmt = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
  const fmt = (d, opts) => {
    try { return d ? new Date(d + 'T12:00:00').toLocaleDateString(dateFmt, opts) : ''; } catch { return ''; }
  };
  const parts = [];
  ['pt', 'en', 'es'].forEach(l => {
    parts.push(tour.name?.[l], tour.destination?.[l], stripHtml(tour.description?.[l]));
    parts.push(tour[`card_teaser_${l}`], tour[`group_label_${l}`]);
  });
  (tour.highlights || []).forEach(h => parts.push(h.pt, h.en, h.es));
  (tour.guides || []).forEach(g => parts.push(g.name));
  const d1 = tour.departure_date, d2 = tour.arrival_date;
  [d1, d2].forEach(d => {
    parts.push(d || '');
    parts.push(fmt(d, { month: 'long', year: 'numeric' }));
    parts.push(fmt(d, { month: 'short', year: 'numeric' }));
    parts.push(fmt(d, { day: '2-digit', month: 'long', year: 'numeric' }));
    if (d) parts.push(d.slice(0, 4), d.slice(5, 7) + '/' + d.slice(0, 4));
  });
  return normalizeForSearch(parts.join(' '));
};

/**
 * Compact list row — used when the user picks "List" view on the home
 * or /pilgrimages. Keeps the Belmond aesthetic (serif title, gold accents)
 * but minimizes vertical real-estate so the user can scan dozens of trips.
 */
const TourListRow = ({ tour, language = 'pt' }) => {
  const trackProps = useTourTracking(tour.id);
  const navigate = useNavigate();
  const name = tour.name?.[language] || tour.name?.pt || '';
  const dest = tour.destination?.[language] || tour.destination?.pt || '';
  const img = tour.image_list_view || tour.image_card || tour.image;
  const resolvedImg = img?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;
  const dateFmt = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
  const dayMonth = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString(dateFmt, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const tagLabels = {
    esgotado: { pt: 'Esgotado', en: 'Sold Out', es: 'Agotado' },
    ultimas_vagas: { pt: 'Últimas Vagas', en: 'Last Spots', es: 'Últimas Plazas' },
    em_breve: { pt: 'Em Breve', en: 'Coming Soon', es: 'Próximamente' },
    concluida: { pt: 'Já Realizada', en: 'Concluded', es: 'Realizada' },
  };
  const statusTag = tour.is_past ? 'concluida' : tour.status_tag;
  const isPrivateGated = tour.visibility === 'private_gated';

  return (
    <button
      type="button"
      {...trackProps}
      onClick={() => navigate(`/tour/${tour.id}`)}
      className="w-full text-left bg-white hover:shadow-md border transition flex items-stretch gap-0 overflow-hidden group"
      style={{ borderColor: `${alpha(GOLD, '44')}` }}
      data-testid={`tour-list-row-${tour.id}`}
    >
      <div className="w-24 sm:w-40 shrink-0 bg-gray-100 relative overflow-hidden" style={{ aspectRatio: '1 / 1' }}>
        {resolvedImg && (
          <img src={resolvedImg} alt={name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        )}
        {statusTag && tagLabels[statusTag] && (
          <div className="absolute top-1.5 left-1.5 text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 font-medium"
            style={{
              background: statusTag === 'esgotado' ? '#8c1d1d' : statusTag === 'ultimas_vagas' ? '#b85c00' : statusTag === 'em_breve' ? '#2d5f3f' : '#4a4a4a',
              color: '#fff',
            }}>
            {tagLabels[statusTag][language]}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 p-3 sm:p-4 flex flex-col justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[9px] sm:text-[10px] tracking-[0.25em] uppercase mb-1 opacity-70" style={{ color: NAVY }}>
            <MapPin className="w-3 h-3 shrink-0" style={{ color: GOLD }} />
            <span className="truncate">{dest}</span>
            {tour.duration > 0 && (
              <><span className="opacity-40">·</span><Clock className="w-3 h-3 shrink-0" />{tour.duration} {language === 'pt' ? 'dias' : language === 'en' ? 'days' : 'días'}</>
            )}
          </div>
          <h3 style={SERIF} className="text-base sm:text-xl font-light leading-tight line-clamp-2" data-testid={`tour-list-name-${tour.id}`}>
            {name}
          </h3>
          {(tour.departure_date || tour.arrival_date) && (
            <div className="flex items-center gap-1.5 text-[11px] sm:text-xs opacity-70 mt-1" style={{ color: NAVY }}>
              <Calendar className="w-3 h-3" style={{ color: GOLD }} />
              <span>{dayMonth(tour.departure_date || tour.arrival_date)}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <div style={SERIF} className="text-sm sm:text-lg font-bold leading-none" data-testid={`tour-list-price-${tour.id}`}>
            <span className="text-[10px] sm:text-xs align-top opacity-70">{tour.currency === 'EUR' ? '€' : 'US$ '}</span>
            {tour.price?.toLocaleString('en-US')}
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] tracking-[0.3em] uppercase font-semibold opacity-70 group-hover:opacity-100 transition" style={{ color: NAVY }}>
            {language === 'pt' ? 'Ver' : language === 'en' ? 'View' : 'Ver'} <ArrowRight className="w-3 h-3" />
          </span>
          {isPrivateGated && (
            <Lock className="sm:hidden w-3 h-3 opacity-60" />
          )}
        </div>
      </div>
    </button>
  );
};

export default TourListRow;
